/**
 * manager-collection.js
 * Gerencia páginas de coleção: filtros AJAX, infinite scroll e grid switching.
 * Disponível como window.CollectionManager.
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   Grade:
 *     data-collection-grid           → container da grade (id="CollectionGrid")
 *     data-infinite-scroll="true"    → ativa infinite scroll
 *     data-product-count             → exibe contagem atual de produtos carregados
 *
 *   Grid switching (desktop):
 *     data-grid-switcher             → wrapper dos botões
 *     data-grid-toggle               → alterna 3/4 colunas
 *     .grid-icon-3 / .grid-icon-4   → ícones dos botões (hidden conforme estado)
 *
 *   Grid switching (mobile):
 *     data-grid-switcher-mobile      → wrapper dos botões
 *     data-grid-toggle-mobile        → alterna 1/2 colunas
 *     .grid-icon-mobile-1 / .grid-icon-mobile-2
 *
 *   Drawer de filtros (usa DrawerManager — data-drawer="filters"):
 *     data-filter-remove             → remove tag de filtro ativo (lê href)
 *     data-filter-clear-all          → remove todos os filtros (lê href)
 *     data-filter-price-submit       → aplica filtro de preço
 *     data-filter-count              → exibe contagem de resultados
 *
 *   Ordenação (dropdown na barra de filtros):
 *     data-filter-sort-wrapper       → wrapper do <details>; re-renderizado no AJAX
 *                                      (o dropdown fecha sozinho ao aplicar)
 *     data-filter-sort               → link de opção de ordenação (lê href)
 *
 *   Paginação / infinite scroll:
 *     data-collection-pagination     → container da paginação
 *     data-infinite-scroll-loading   → indicador de loading (hidden por padrão)
 *
 * ─── Estrutura esperada do filtro ─────────────────────────────────────────
 *
 *   <form id="CollectionFiltersForm">
 *     <!-- inputs de filtro (checkboxes, selects) -->
 *     <!-- .filter-price-input: ignorado no change, aguarda data-filter-price-submit -->
 *   </form>
 *
 *   <!-- Acordeões de filtro: usar <details>/<summary> nativo -->
 *   <details>
 *     <summary>Tamanho</summary>
 *     <div><!-- opções --></div>
 *   </details>
 *
 * ─── Persistência do grid ─────────────────────────────────────────────────
 *
 *   localStorage:
 *     'wc-grid-cols'        → '3' | '4'   (desktop, default '4')
 *     'wc-grid-cols-mobile' → '1' | '2'   (mobile,  default '2')
 */

class CollectionManager {
  constructor() {
    this._grid       = document.querySelector('[data-collection-grid]');
    if (!this._grid) return;

    this._pagination = document.querySelector('[data-collection-pagination]');
    this._isLoading  = false;
    this._infiniteObserver = null;
    this._infiniteSentinel = null;
    this._infiniteFallbackBound = false;
    this._localSearch = null;
    this._localSearchInput = null;
    this._localSearchClear = null;
    this._localSearchMatches = [];
    this._localSearchMatchIndex = -1;
    this._viewportFitObserver = null;
    this._onViewportFitResize = null;
    this._onViewportFitMediaLoad = null;
    this._dockPositionRaf = null;
    this._dockHeaderObserver = null;
    this._dockSizeObserver = null;
    this._floatingDock = null;
    this._quickFilters = null;
    this._filtersBar = null;
    this._onDockPositionChange = this._onDockPositionChange.bind(this);
    this._onQuickFilterClick = this._onQuickFilterClick.bind(this);

    this._onScroll       = this._onScroll.bind(this);
    this._onFilterClick  = this._onFilterClick.bind(this);
    this._onLocalSearchKeyDown = this._onLocalSearchKeyDown.bind(this);

    this._init();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    this._initViewportFit();
    this._initGridSwitcher();
    this._initFilters();
    this._initLocalSearch();
    this._initFloatingDocks();

    if (this._grid.dataset.infiniteScroll === 'true') {
      this._initInfiniteScroll();
    }

    this._updateCount();
  }

  _initFloatingDocks() {
    const quickFilters = document.querySelector('.collectionQuickFilters');
    const filtersBar = document.querySelector('.collectionFilters[data-results-context="collection"], .collectionFilters[data-results-context="search"]');
    if (!filtersBar) return;

    let dock = document.querySelector('.collectionFloatingDock');
    if (!dock) {
      dock = document.createElement('div');
      dock.className = 'collectionFloatingDock';
      dock.setAttribute('data-collection-floating-dock', '');
      document.body.appendChild(dock);
    }

    if (!dock.contains(filtersBar)) dock.appendChild(filtersBar);
    if (quickFilters && !dock.contains(quickFilters)) dock.appendChild(quickFilters);

    this._floatingDock = dock;
    this._quickFilters = quickFilters;
    this._filtersBar = filtersBar;

    if (quickFilters) quickFilters.addEventListener('click', this._onQuickFilterClick);

    this._syncDockOrder();
    this._updateFloatingDockPosition();
    this._sortQuickFiltersByCount();
    this._updateQuickFilterMetrics();
    if (quickFilters?.dataset.countSource === 'legacy') {
      quickFilters.classList.add('is-counted');
    }
    this._hydrateQuickFiltersFromBase();

    window.addEventListener('resize', this._onDockPositionChange, { passive: true });
    window.addEventListener('scroll', this._onDockPositionChange, { passive: true });

    if ('ResizeObserver' in window) {
      this._dockHeaderObserver = new ResizeObserver(this._onDockPositionChange);
      document.querySelectorAll('[data-tipbar], .headerModel2Header__desktop, .headerModel2Header__mobile, [data-header-root]').forEach((element) => {
        this._dockHeaderObserver.observe(element);
      });

      this._dockSizeObserver = new ResizeObserver(() => this._updateQuickFilterMetrics());
      if (quickFilters) this._dockSizeObserver.observe(quickFilters);
      this._dockSizeObserver.observe(filtersBar);
    }

    dock.classList.add('is-ready');
  }

  _onDockPositionChange() {
    if (this._dockPositionRaf) return;
    this._dockPositionRaf = window.requestAnimationFrame(() => {
      this._dockPositionRaf = null;
      this._syncDockOrder();
      this._updateFloatingDockPosition();
      this._updateQuickFilterMetrics();
    });
  }

  _syncDockOrder() {
    const dock = this._floatingDock;
    const quickFilters = this._quickFilters;
    const filtersBar = this._filtersBar;
    if (!dock || !filtersBar) return;

    if (dock.firstElementChild !== filtersBar) {
      dock.insertBefore(filtersBar, dock.firstElementChild);
    }

    if (quickFilters && filtersBar.nextElementSibling !== quickFilters) {
      dock.insertBefore(quickFilters, filtersBar.nextElementSibling);
    }
  }

  _updateFloatingDockPosition() {
    const candidates = document.querySelectorAll('[data-tipbar], .headerModel2Header__desktop, .headerModel2Header__mobile, [data-header-root]');
    let headerBottom = 0;

    candidates.forEach((element) => {
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return;

      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;

      headerBottom = Math.max(headerBottom, rect.bottom);
    });

    const dockGap = window.matchMedia('(max-width: 749px)').matches ? 4 : 6;
    document.documentElement.style.setProperty('--collection-dock-top', `${Math.max(0, Math.ceil(headerBottom) + dockGap)}px`);
  }

  _sortQuickFiltersByCount() {
    const nav = this._quickFilters || document.querySelector('.collectionQuickFilters');
    const list = nav?.querySelector('.collectionQuickFilters__list');
    if (!list) return;

    const items = [...list.querySelectorAll('.collectionQuickFilters__item')];

    items.sort((a, b) => {
      const countA = Number.parseInt(a.dataset.categoryCount || '0', 10) || 0;
      const countB = Number.parseInt(b.dataset.categoryCount || '0', 10) || 0;

      if (countA !== countB) return countB - countA;

      const labelA = a.textContent?.trim() || '';
      const labelB = b.textContent?.trim() || '';
      return labelA.localeCompare(labelB, 'pt-BR', { sensitivity: 'base' });
    });

    items.forEach((item) => list.appendChild(item));
  }

  _updateQuickFilterMetrics() {
    const nav = this._quickFilters || document.querySelector('.collectionQuickFilters');
    if (!nav) return;

    const items = [...nav.querySelectorAll('.collectionQuickFilters__item')];
    const moreButton = nav.querySelector('[data-collection-quick-filters-more]');
    nav.classList.toggle('has-more', items.length > 6);
    if (!items.length) return;

    const visibleItems = items.slice(0, 6);
    const itemWidth = visibleItems.reduce((total, item) => total + Math.ceil(item.getBoundingClientRect().width), 0);
    const moreWidth = items.length > 6 && moreButton ? Math.ceil(moreButton.getBoundingClientRect().width) : 0;
    const navStyle = window.getComputedStyle(nav);
    const horizontalPadding = (parseFloat(navStyle.paddingLeft) || 0) + (parseFloat(navStyle.paddingRight) || 0);
    const collapsedWidth = Math.ceil(itemWidth + moreWidth + horizontalPadding + 2);

    nav.style.setProperty('--quickfilters-collapsed-width', `${collapsedWidth}px`);
  }

  _onQuickFilterClick(event) {
    const moreButton = event.target.closest('[data-collection-quick-filters-more]');
    if (moreButton && this._quickFilters?.contains(moreButton)) {
      event.preventDefault();
      const expanded = this._quickFilters.classList.toggle('is-expanded');
      moreButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      return;
    }

    const link = event.target.closest('[data-collection-category-link]');
    if (!link || !this._quickFilters?.contains(link)) return;

    event.preventDefault();

    const mode = link.dataset.categoryMode || 'filter';
    const paramName = link.dataset.categoryParam || 'filter.p.tag';
    const categoryValue = link.dataset.categoryValue || '';
    if (!categoryValue) return;

    const item = link.closest('.collectionQuickFilters__item');
    const isActive = item?.classList.contains('is-active');
    const baseUrl = new URL(this._quickFilters.dataset.baseUrl || window.location.pathname, window.location.origin);
    const currentUrl = new URL(window.location.href);

    currentUrl.pathname = baseUrl.pathname;
    currentUrl.searchParams.delete('page');

    if (mode === 'legacy') {
      const categoryHandle = link.dataset.categoryHandle || '';
      if (!isActive && categoryHandle) {
        currentUrl.pathname = `${baseUrl.pathname.replace(/\/$/, '')}/${categoryHandle}`;
      }
      window.location.assign(currentUrl.toString());
      return;
    }

    const currentValues = currentUrl.searchParams.getAll(paramName);

    currentUrl.searchParams.delete(paramName);
    currentValues.forEach((value) => {
      if (!value.startsWith('cat_') || value !== categoryValue) {
        currentUrl.searchParams.append(paramName, value);
      }
    });

    if (!isActive) {
      currentUrl.searchParams.append(paramName, categoryValue);
    }

    window.location.assign(currentUrl.toString());
  }

  async _hydrateQuickFiltersFromBase() {
    const nav = this._quickFilters;
    if (!nav) return;

    const activeCategory = nav.dataset.activeCategory || '';
    const sectionId = nav.dataset.sectionId || '';
    const baseUrl = nav.dataset.baseUrl || '';
    const countSource = nav.dataset.countSource || 'native';
    if (!sectionId || !baseUrl) return;

    if (countSource === 'native' && !activeCategory) {
      this._sortQuickFiltersByCount();
      this._updateQuickFilterMetrics();
      return;
    }

    try {
      const currentUrl = new URL(window.location.href);
      const previewThemeId = currentUrl.searchParams.get('preview_theme_id');
      const resultsContext = nav.dataset.resultsContext || this._filtersBar?.dataset.resultsContext || 'collection';

      const requestBaseUrl = (() => {
        if (resultsContext !== 'search') {
          return new URL(baseUrl, window.location.origin);
        }

        const url = new URL(currentUrl.toString());
        url.searchParams.delete('page');
        const retainedTagValues = url.searchParams
          .getAll('filter.p.tag')
          .filter((value) => !value.startsWith('cat_'));
        url.searchParams.delete('filter.p.tag');
        retainedTagValues.forEach((value) => url.searchParams.append('filter.p.tag', value));
        return url;
      })();

      const buildSectionUrl = (page) => {
        const url = new URL(requestBaseUrl.toString());
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
        url.searchParams.set('section_id', sectionId);
        if (page > 1) url.searchParams.set('page', String(page));
        else url.searchParams.delete('page');
        return url;
      };

      const cacheUrl = new URL(requestBaseUrl.toString());
      cacheUrl.searchParams.delete('section_id');
      cacheUrl.searchParams.delete('preview_theme_id');
      cacheUrl.searchParams.delete('page');
      const cacheKey = `iorane-category-counts:${cacheUrl.toString()}`;
      let cachedCounts = null;
      if (countSource === 'legacy') {
        try {
          cachedCounts = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        } catch (error) {
          cachedCounts = null;
        }
      }

      const firstResponse = await fetch(buildSectionUrl(1).toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!firstResponse.ok) return;

      const firstHtml = await firstResponse.text();
      const firstDocument = new DOMParser().parseFromString(firstHtml, 'text/html');
      const sourceNav = firstDocument.querySelector('.collectionQuickFilters');
      if (!sourceNav) return;

      nav.innerHTML = sourceNav.innerHTML;
      nav.className = sourceNav.className;
      nav.dataset.baseUrl = baseUrl;
      nav.dataset.sectionId = sectionId;
      nav.dataset.activeCategory = activeCategory;
      nav.dataset.countSource = sourceNav.dataset.countSource || countSource;
      nav.dataset.resultsContext = sourceNav.dataset.resultsContext || resultsContext;
      nav.dataset.totalPages = sourceNav.dataset.totalPages || '1';
      nav.dataset.currentPage = '1';

      const categoryParam = nav.querySelector('[data-collection-category-link]')?.dataset.categoryParam || 'filter.p.tag';
      const activeCategories = currentUrl.searchParams
        .getAll(categoryParam)
        .filter((value) => value.startsWith('cat_'));
      if (!activeCategories.length && activeCategory) activeCategories.push(activeCategory);

      nav.querySelectorAll('[data-collection-category-link]').forEach((candidate) => {
        const item = candidate.closest('.collectionQuickFilters__item');
        item?.classList.toggle('is-active', activeCategories.includes(candidate.dataset.categoryValue || ''));
      });

      if ((sourceNav.dataset.countSource || countSource) === 'legacy') {
        if (cachedCounts && typeof cachedCounts === 'object') {
          this._applyQuickFilterCounts(cachedCounts);
        } else {
          const totalPages = Math.max(1, Number.parseInt(sourceNav.dataset.totalPages || '1', 10) || 1);
          const counts = this._extractQuickFilterCounts(sourceNav);

          const pageNumbers = [];
          for (let page = 2; page <= totalPages; page += 1) pageNumbers.push(page);

          const concurrency = 4;
          for (let index = 0; index < pageNumbers.length; index += concurrency) {
            const batch = pageNumbers.slice(index, index + concurrency);
            const responses = await Promise.all(batch.map(async (page) => {
              const response = await fetch(buildSectionUrl(page).toString(), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
              });
              if (!response.ok) return null;
              const html = await response.text();
              const documentFragment = new DOMParser().parseFromString(html, 'text/html');
              return documentFragment.querySelector('.collectionQuickFilters');
            }));

            responses.filter(Boolean).forEach((pageNav) => {
              this._mergeQuickFilterItems(pageNav);
              const pageCounts = this._extractQuickFilterCounts(pageNav);
              Object.entries(pageCounts).forEach(([value, count]) => {
                counts[value] = (counts[value] || 0) + count;
              });
            });
          }

          this._applyQuickFilterCounts(counts);
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(counts));
          } catch (error) {
          }
        }
      }

      this._sortQuickFiltersByCount();
      this._updateQuickFilterMetrics();
      if (nav.dataset.resultsContext === 'search') {
        const itemCount = nav.querySelectorAll('.collectionQuickFilters__item').length;
        nav.hidden = itemCount < 3;
      }
      nav.classList.add('is-counted');
    } catch (error) {
      console.warn('Could not hydrate collection category chips.', error);
      nav.classList.add('is-counted');
    }
  }

  _extractQuickFilterCounts(nav) {
    const counts = {};
    nav.querySelectorAll('[data-collection-category-link]').forEach((link) => {
      const value = link.dataset.categoryValue || '';
      const item = link.closest('.collectionQuickFilters__item');
      const count = Number.parseInt(item?.dataset.categoryCount || '0', 10) || 0;
      if (value) counts[value] = count;
    });
    return counts;
  }

  _mergeQuickFilterItems(sourceNav) {
    const nav = this._quickFilters;
    const targetList = nav?.querySelector('.collectionQuickFilters__list');
    const sourceList = sourceNav?.querySelector('.collectionQuickFilters__list');
    if (!nav || !targetList || !sourceList) return;

    const existingValues = new Set(
      [...targetList.querySelectorAll('[data-collection-category-link]')]
        .map((link) => link.dataset.categoryValue || '')
        .filter(Boolean)
    );

    sourceList.querySelectorAll('.collectionQuickFilters__item').forEach((item) => {
      const link = item.querySelector('[data-collection-category-link]');
      const value = link?.dataset.categoryValue || '';
      if (!value || existingValues.has(value)) return;
      targetList.appendChild(item.cloneNode(true));
      existingValues.add(value);
    });
  }

  _applyQuickFilterCounts(counts) {
    const nav = this._quickFilters;
    if (!nav) return;

    nav.querySelectorAll('[data-collection-category-link]').forEach((link) => {
      const value = link.dataset.categoryValue || '';
      const item = link.closest('.collectionQuickFilters__item');
      if (!item || !value) return;
      item.dataset.categoryCount = String(counts[value] || 0);
    });
  }

  _initViewportFit() {
    if (this._grid.dataset.collectionViewportFit !== 'true') return;

    this._onViewportFitResize = this._updateViewportFit.bind(this);
    this._onViewportFitMediaLoad = (event) => {
      if (!event.target.matches?.('.collectionMedia__video, .collectionMedia__image')) return;
      if (!event.target.closest('.collection__products--viewport-fit .collectionMedia')) return;
      this._updateViewportFitMedia();
    };
    this._updateViewportFit();
    window.addEventListener('resize', this._onViewportFitResize, { passive: true });
    document.addEventListener('load', this._onViewportFitMediaLoad, true);
    document.addEventListener('loadedmetadata', this._onViewportFitMediaLoad, true);

    if ('ResizeObserver' in window) {
      this._viewportFitObserver = new ResizeObserver(this._onViewportFitResize);
      this._viewportFitObserver.observe(this._grid);

      const filtersBar = document.querySelector('.collectionFilters');
      if (filtersBar) this._viewportFitObserver.observe(filtersBar);
    }
  }

  _updateViewportFit() {
    const firstGrid = this._grid.querySelector('.collection__double-grid--viewport-anchor');
    if (!firstGrid) return;

    const topOffset = Math.max(0, firstGrid.getBoundingClientRect().top);
    const availableHeight = Math.max(0, window.innerHeight - topOffset);
    this._grid.style.setProperty('--collection-grid-viewport-height', `${availableHeight}px`);
    this._updateViewportFitMedia();
  }

  _updateViewportFitMedia() {
    this._grid.querySelectorAll('.collectionMedia').forEach((card) => {
      const overlay = card.querySelector('.collectionMedia__overlay');
      const activeLayer = [...card.querySelectorAll('.collectionMedia__media')]
        .find((layer) => window.getComputedStyle(layer).display !== 'none');
      const media = activeLayer?.querySelector('.collectionMedia__video, .collectionMedia__image, .collectionMedia__placeholder');

      if (!overlay || !activeLayer || !media) return;

      const mediaBounds = activeLayer.getBoundingClientRect();
      let overlayWidth = mediaBounds.width;
      let overlayHeight = mediaBounds.height;

      if (!mediaBounds.width || !mediaBounds.height) return;

      if (!media.matches('.collectionMedia__placeholder')) {
        const intrinsicWidth = media instanceof HTMLVideoElement ? media.videoWidth : media.naturalWidth;
        const intrinsicHeight = media instanceof HTMLVideoElement ? media.videoHeight : media.naturalHeight;

        if (!intrinsicWidth || !intrinsicHeight) return;

        const scale = Math.min(mediaBounds.width / intrinsicWidth, mediaBounds.height / intrinsicHeight);
        overlayWidth = intrinsicWidth * scale;
        overlayHeight = intrinsicHeight * scale;
      }

      overlay.style.setProperty('--collection-media-overlay-width', `${overlayWidth}px`);
      overlay.style.setProperty('--collection-media-overlay-height', `${overlayHeight}px`);
      overlay.classList.add('is-viewport-fit-sized');
    });
  }

  _initLocalSearch() {
    this._localSearch = document.querySelector('[data-collection-local-search]');
    if (!this._localSearch) return;


    this._localSearchInput = this._localSearch.querySelector('[data-collection-local-search-input]');
    this._localSearchClear = this._localSearch.querySelector('[data-collection-local-search-clear]');

    if (!this._localSearchInput) return;

    this._localSearchInput.addEventListener('input', () => {
      this._applyLocalSearch();
    });
    this._localSearchClear?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this._clearLocalSearch();
      this._localSearchInput.focus();
    });
    document.addEventListener('keydown', this._onLocalSearchKeyDown);
  }

  _clearLocalSearch() {
    if (!this._localSearchInput) return;
    this._localSearchInput.value = '';
    this._applyLocalSearch();
  }

  _onLocalSearchKeyDown(event) {
    if (!this._localSearchInput) return;

    if (event.key === 'Enter' && document.activeElement === this._localSearchInput) {
      event.preventDefault();
      this._focusNextLocalSearchMatch(event.shiftKey);
      return;
    }

    if (event.key === 'Escape' && document.activeElement === this._localSearchInput) {
      if (this._localSearchInput.value) {
        this._clearLocalSearch();
      } else {
        this._localSearchInput.blur();
      }
    }
  }

  _normalizeLocalSearchText(value) {
    return value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  _applyLocalSearch() {
    if (!this._localSearch || !this._localSearchInput || !this._grid) return;

    const query = this._normalizeLocalSearchText(this._localSearchInput.value);
    this._grid.classList.toggle('is-local-search-active', Boolean(query));
    const cards = [...this._grid.querySelectorAll('[data-product-card]')];
    this._localSearchMatches = [];
    this._localSearchMatchIndex = -1;
    let visibleCount = 0;

    cards.forEach((card) => {
      const title = card.querySelector('[data-product-card-title]')?.textContent || '';
      const swatches = [...card.querySelectorAll('[data-product-card-swatch]')];
      const searchableTitles = [
        title,
        ...swatches.map((swatch) => swatch.dataset.title || ''),
      ];
      const matches = !query || searchableTitles.some((value) => this._normalizeLocalSearchText(value).includes(query));

      if (!query && card.dataset.localSearchOriginalHandle) {
        const originalSwatch = swatches.find((swatch) => swatch.dataset.handle === card.dataset.localSearchOriginalHandle);
        if (originalSwatch && !originalSwatch.classList.contains('is-active')) {
          originalSwatch.click();
        }
        delete card.dataset.localSearchOriginalHandle;
      }

      if (query && matches) {
        if (!card.dataset.localSearchOriginalHandle) {
          const originalSwatch = swatches.find((swatch) => swatch.classList.contains('is-active')) || swatches[0];
          if (originalSwatch) card.dataset.localSearchOriginalHandle = originalSwatch.dataset.handle || '';
        }

        const matchingSwatch = swatches.find((swatch) =>
          this._normalizeLocalSearchText(swatch.dataset.title || '').includes(query)
        );
        if (matchingSwatch && !matchingSwatch.classList.contains('is-active')) {
          matchingSwatch.click();
        }
      }

      card.hidden = !matches;
      card.classList.toggle('is-local-search-hidden', !matches);
      card.classList.remove('is-local-search-match');
      if (matches) {
        visibleCount += 1;
        if (query) this._localSearchMatches.push(card);
      }
    });

    this._grid.querySelectorAll('.collection__double-grid, .collection__editorial-grid, .collection__normal-grid').forEach((group) => {
      const hasCards = group.querySelector('[data-product-card]');
      const hasVisibleCards = group.querySelector('[data-product-card]:not([hidden])');
      const isHidden = Boolean(query && hasCards && !hasVisibleCards);
      group.hidden = isHidden;
      group.classList.toggle('is-local-search-hidden', isHidden);
    });

    if (this._localSearchClear) {
      this._localSearchClear.hidden = !query;
    }

    const status = this._localSearch.querySelector('[data-collection-local-search-status]');
    if (status) {
      if (!query) {
        status.textContent = '';
      } else if (visibleCount > 0) {
        status.textContent = (this._localSearch.dataset.resultsTemplate || '').replace('__COUNT__', visibleCount);
      } else {
        status.textContent = this._localSearch.dataset.noResultsLabel || '';
      }
    }

    this._updateCount(visibleCount);
  }

  _focusNextLocalSearchMatch(reverse = false) {
    if (!this._localSearchMatches.length) return;

    const total = this._localSearchMatches.length;
    this._localSearchMatchIndex = reverse
      ? (this._localSearchMatchIndex - 1 + total) % total
      : (this._localSearchMatchIndex + 1) % total;

    this._localSearchMatches.forEach((card, index) => {
      card.classList.toggle('is-local-search-match', index === this._localSearchMatchIndex);
    });

    const match = this._localSearchMatches[this._localSearchMatchIndex];
    match.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }

  // ─── Grid Switching ───────────────────────────────────────────────────────

  _initGridSwitcher() {
    const toggleDesktop = document.querySelector('[data-grid-toggle]');
    const toggleMobile  = document.querySelector('[data-grid-toggle-mobile]');

    if (toggleDesktop) {
      const saved = localStorage.getItem('wc-grid-cols') || '4';
      this._applyGrid(saved, false);
      toggleDesktop.addEventListener('click', () => {
        const current = localStorage.getItem('wc-grid-cols') || '4';
        this._applyGrid(current === '4' ? '3' : '4');
      });
    }

    if (toggleMobile) {
      const saved = localStorage.getItem('wc-grid-cols-mobile') || '2';
      this._applyGridMobile(saved, false);
      toggleMobile.addEventListener('click', () => {
        const current = localStorage.getItem('wc-grid-cols-mobile') || '2';
        this._applyGridMobile(current === '2' ? '1' : '2');
      });
    }
  }

  _applyGrid(cols, save = true) {
    const prev = cols === '4' ? '3' : '4';
    this._grid.classList.remove(`md:grid-cols-${prev}`);
    this._grid.classList.add(`md:grid-cols-${cols}`);
    if (save) localStorage.setItem('wc-grid-cols', cols);
    document.querySelector('.grid-icon-3')?.classList.toggle('hidden', cols === '3');
    document.querySelector('.grid-icon-4')?.classList.toggle('hidden', cols === '4');
  }

  _applyGridMobile(cols, save = true) {
    const prev = cols === '2' ? '1' : '2';
    this._grid.classList.remove(`grid-cols-${prev}`);
    this._grid.classList.add(`grid-cols-${cols}`);
    if (save) localStorage.setItem('wc-grid-cols-mobile', cols);
    document.querySelector('.grid-icon-mobile-1')?.classList.toggle('hidden', cols === '1');
    document.querySelector('.grid-icon-mobile-2')?.classList.toggle('hidden', cols === '2');
  }

  // ─── Filtros AJAX ─────────────────────────────────────────────────────────

  _initFilters() {
    const form = document.querySelector('#CollectionFiltersForm');
    if (form) {
      form.addEventListener('change', e => {
        if (e.target.classList.contains('filter-price-input')) return;
        this._applyFilters();
      });
    }

    document.addEventListener('click', this._onFilterClick);
  }

  _onFilterClick(e) {
    const removeBtn = e.target.closest('[data-filter-remove]');
    if (removeBtn) { e.preventDefault(); this._applyFilters(removeBtn.href); return; }

    const clearAll = e.target.closest('[data-filter-clear-all]');
    if (clearAll) { e.preventDefault(); this._applyFilters(clearAll.href); return; }

    const priceSubmit = e.target.closest('[data-filter-price-submit]');
    if (priceSubmit) { e.preventDefault(); this._applyFilters(); return; }

    const sortLink = e.target.closest('[data-filter-sort]');
    if (sortLink) {
      e.preventDefault();
      const filtersBar = sortLink.closest('.collectionFilters');
      if (filtersBar?.dataset.resultsContext === 'search') {
        const currentUrl = new URL(window.location.href);
        const selectedSort = new URL(sortLink.href, window.location.href).searchParams.get('sort_by');
        if (selectedSort) currentUrl.searchParams.set('sort_by', selectedSort);
        currentUrl.searchParams.delete('page');
        this._applyFilters(currentUrl.toString());
      } else {
        this._applyFilters(sortLink.href);
      }
      return;
    }

    // Fecha o dropdown de ordenação ao clicar fora dele
    const openSort = document.querySelector('[data-filter-sort-wrapper] details[open]');
    if (openSort && !openSort.contains(e.target)) openSort.removeAttribute('open');
  }

  async _applyFilters(customUrl) {
    const form = document.querySelector('#CollectionFiltersForm');
    const url  = customUrl || `${window.location.pathname}?${new URLSearchParams(new FormData(form)).toString()}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();
      const doc  = new DOMParser().parseFromString(text, 'text/html');

      this._replaceInner('#CollectionFiltersForm',                      doc);
      this._replaceInner('[data-filter-sort-wrapper]',                  doc);
      this._replaceInner('[data-filter-count]',                         doc);
      this._replaceInner('[data-collection-grid]',                      doc);
      this._replaceInner('[data-collection-pagination]',                doc);
      this._replaceInner('.filter-drawer__active-facets-wrapper',       doc);

      history.pushState({ path: url }, '', url);
      if (this._localSearch) {
        this._applyLocalSearch();
      } else {
        this._updateCount();
      }

      if (this._grid?.dataset.infiniteScroll === 'true') {
        this._initInfiniteScroll();
      }
    } catch (err) {
      console.error('[CollectionManager] Filter failed:', err);
    }
  }

  _replaceInner(selector, doc) {
    const newEl  = doc.querySelector(selector);
    const currEl = document.querySelector(selector);
    if (newEl && currEl) {
      currEl.innerHTML = newEl.innerHTML;
      if (selector === '[data-collection-grid]') {
        this._updateViewportFit();
        window.ProductManager?.init(currEl);
      }
    }
  }

  // ─── Infinite Scroll ──────────────────────────────────────────────────────

  _initInfiniteScroll() {
    if (!this._grid || !this._pagination) return;

    this._teardownInfiniteScroll();

    this._pagination.hidden = true;
    this._pagination.setAttribute('aria-hidden', 'true');

    let loading = document.querySelector('[data-infinite-scroll-loading]');
    if (!loading) {
      loading = document.createElement('div');
      loading.setAttribute('data-infinite-scroll-loading', '');
      loading.setAttribute('hidden', '');
      loading.setAttribute('aria-hidden', 'true');
      this._pagination.before(loading);
    }

    const sentinel = document.createElement('div');
    sentinel.setAttribute('data-infinite-scroll-sentinel', '');
    sentinel.setAttribute('aria-hidden', 'true');
    this._pagination.before(sentinel);
    this._infiniteSentinel = sentinel;

    if (!this._pagination.querySelector('.next a, a.next')) {
      this._finishInfiniteScroll();
      return;
    }

    if ('IntersectionObserver' in window) {
      this._infiniteObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._loadNextPage();
        }
      }, {
        root: null,
        rootMargin: '1800px 0px',
        threshold: 0
      });

      this._infiniteObserver.observe(sentinel);
    } else {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._infiniteFallbackBound = true;
      this._onScroll();
    }
  }

  _teardownInfiniteScroll() {
    this._infiniteObserver?.disconnect();
    this._infiniteObserver = null;

    if (this._infiniteFallbackBound) {
      window.removeEventListener('scroll', this._onScroll);
      this._infiniteFallbackBound = false;
    }

    this._infiniteSentinel?.remove();
    this._infiniteSentinel = null;
  }

  _onScroll() {
    if (this._isLoading || !this._infiniteSentinel) return;
    const rect = this._infiniteSentinel.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 1800) {
      this._loadNextPage();
    }
  }

  async _loadNextPage() {
    if (this._isLoading) return;

    const nextLink = this._pagination?.querySelector('.next a, a.next');
    if (!nextLink) {
      this._finishInfiniteScroll();
      return;
    }

    this._isLoading = true;
    this._setInfiniteScrollLoading(true);
    let hasNextPage = false;

    try {
      const res = await fetch(nextLink.href, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = doc.querySelector('[data-collection-grid]');

      if (!newGrid) throw new Error('Next page grid not found');

      [...newGrid.children].forEach((child) => this._grid.appendChild(child));
      window.ProductManager?.init(this._grid);

      const newPagination = doc.querySelector('[data-collection-pagination]');
      if (this._pagination && newPagination) {
        this._pagination.innerHTML = newPagination.innerHTML;
      }

      if (this._localSearch) {
        this._applyLocalSearch();
      } else {
        this._updateCount();
      }

      hasNextPage = Boolean(this._pagination?.querySelector('.next a, a.next'));
      if (!hasNextPage) {
        this._finishInfiniteScroll();
      }
    } catch (err) {
      console.error('[CollectionManager] Infinite scroll failed:', err);
      this._disableInfiniteScrollFallback();
    } finally {
      this._isLoading = false;
      this._setInfiniteScrollLoading(false);

      if (hasNextPage) {
        window.requestAnimationFrame(() => this._maybeLoadNextPage());
      }
    }
  }

  _maybeLoadNextPage() {
    if (this._isLoading || !this._infiniteSentinel) return;
    const rect = this._infiniteSentinel.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 1800) {
      this._loadNextPage();
    }
  }

  _finishInfiniteScroll() {
    this._infiniteObserver?.disconnect();
    this._infiniteObserver = null;

    if (this._infiniteFallbackBound) {
      window.removeEventListener('scroll', this._onScroll);
      this._infiniteFallbackBound = false;
    }

    this._infiniteSentinel?.remove();
    this._infiniteSentinel = null;
    this._setInfiniteScrollLoading(false);

    if (this._pagination) {
      this._pagination.hidden = true;
      this._pagination.setAttribute('aria-hidden', 'true');
    }
  }

  _disableInfiniteScrollFallback() {
    this._teardownInfiniteScroll();
    this._setInfiniteScrollLoading(false);

    if (this._pagination) {
      this._pagination.hidden = false;
      this._pagination.removeAttribute('aria-hidden');
    }
  }

  _setInfiniteScrollLoading(show) {
    const el = document.querySelector('[data-infinite-scroll-loading]');
    if (!el) return;
    show ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  // ─── Contador ─────────────────────────────────────────────────────────────

  _updateCount(visibleCount) {
    const count = typeof visibleCount === 'number'
      ? visibleCount
      : this._grid
        ? [...this._grid.querySelectorAll('.productCard')].filter(card => !card.hidden).length
        : undefined;
    const display = document.querySelector('[data-product-count]');
    if (display && count !== undefined) display.textContent = count;
  }

  destroy() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('scroll', this._onDockPositionChange);
    window.removeEventListener('resize', this._onDockPositionChange);
    document.removeEventListener('click', this._onFilterClick);
    document.removeEventListener('keydown', this._onLocalSearchKeyDown);
    this._quickFilters?.removeEventListener('click', this._onQuickFilterClick);
    this._dockHeaderObserver?.disconnect();
    this._dockSizeObserver?.disconnect();
    if (this._dockPositionRaf) {
      window.cancelAnimationFrame(this._dockPositionRaf);
      this._dockPositionRaf = null;
    }
    if (this._onViewportFitResize) {
      window.removeEventListener('resize', this._onViewportFitResize);
    }
    if (this._onViewportFitMediaLoad) {
      document.removeEventListener('load', this._onViewportFitMediaLoad, true);
      document.removeEventListener('loadedmetadata', this._onViewportFitMediaLoad, true);
    }
    this._viewportFitObserver?.disconnect();
    this._grid?.style.removeProperty('--collection-grid-viewport-height');
  }
}

const initializeCollectionManager = () => {
  window.CollectionManager?.destroy();
  window.CollectionManager = new CollectionManager();
};

const sectionContainsCollectionGrid = (section) =>
  section.matches?.('[data-collection-grid]') || section.querySelector?.('[data-collection-grid]');

document.addEventListener('DOMContentLoaded', initializeCollectionManager);
document.addEventListener('shopify:section:unload', (event) => {
  if (!sectionContainsCollectionGrid(event.target)) return;
  window.CollectionManager?.destroy();
  window.CollectionManager = null;
});
document.addEventListener('shopify:section:load', (event) => {
  if (sectionContainsCollectionGrid(event.target)) initializeCollectionManager();
});
